import { ApiError } from '@eneco/dc/client/ApiError';
import { getDiscontinueIntake, getDiscontinueReasons } from '@eneco/dc/services';
import {
  Products_FineCalculation_DiscontinueIntakeModel,
  Products_FineCalculation_ReasonModel,
  Products_Relocations_DiscontinueProductType,
  ResponseDataDC_Products_Client_Old_Client_Models_DiscontinueReasons,
  ResponseModels_Products_FineCalculation_DiscontinueIntakeModel,
} from '@monorepo-types/dc';
import { assign, fromPromise, setup } from 'xstate';

import { MachineConfig } from '../types';

export interface CancellationReasonFetchValues {
  customerId: number | null;
}

export interface CancellationReasonMachineContextBase<TReason, TIntake> {
  errorMessage?: string;
  reasonStatus?: 'open' | 'expired' | 'accepted' | 'denied';
  reasons: Array<TReason> | undefined;
  discontinueDisallowReason?: string;
  remainingMonths?: number;
  intake?: TIntake;
}

export type CancellationReasonMachineContext = CancellationReasonMachineContextBase<
  Products_FineCalculation_ReasonModel,
  Products_FineCalculation_DiscontinueIntakeModel
>;

interface ProductReasonFetchOutput {
  intakeData: ResponseModels_Products_FineCalculation_DiscontinueIntakeModel;
  reasonsData: ResponseDataDC_Products_Client_Old_Client_Models_DiscontinueReasons;
}

export type CancellationReasonFetchHandler<TOutput> = (
  config: MachineConfig,
  values: CancellationReasonFetchValues,
) => Promise<TOutput>;

const getIntakeService = (
  productType: Products_Relocations_DiscontinueProductType,
  config: MachineConfig,
  customerObject: CancellationReasonFetchValues,
): Promise<ResponseModels_Products_FineCalculation_DiscontinueIntakeModel> =>
  new Promise((resolve, reject) =>
    getDiscontinueIntake({
      businessUnit: config.businessUnit,
      label: config.label,
      customerId: customerObject.customerId!,
      productType,
    })
      .then(response => resolve(response))
      .catch((error: ApiError) => reject(new Error(error?.message))),
  );

const getReasonsService = (
  productType: Products_Relocations_DiscontinueProductType,
  config: MachineConfig,
): Promise<ResponseDataDC_Products_Client_Old_Client_Models_DiscontinueReasons> =>
  new Promise((resolve, reject) =>
    getDiscontinueReasons({
      businessUnit: config.businessUnit,
      label: config.label,
      productType,
    })
      .then(response => resolve(response))
      .catch((error: ApiError) => reject(new Error(error?.message))),
  );

const getValuesFromAttributes = (
  intakeResponse?: Products_FineCalculation_DiscontinueIntakeModel,
  reasonsResponse?: ResponseDataDC_Products_Client_Old_Client_Models_DiscontinueReasons,
): Partial<CancellationReasonMachineContext> => {
  if (!intakeResponse) {
    return {};
  }

  if (intakeResponse.discontinueDisallowReason && intakeResponse.discontinueDisallowReason.length > 0) {
    return {
      discontinueDisallowReason: intakeResponse.discontinueDisallowReason,
      remainingMonths: intakeResponse.dates?.remainingMonths,
      intake: undefined,
      reasons: undefined,
    };
  }

  return {
    intake: intakeResponse,
    reasons: reasonsResponse?.data?.reasons ?? undefined,
    discontinueDisallowReason: undefined,
    remainingMonths: undefined,
  };
};

export const createCancellationReasonMachineWithFetch = <TReason, TIntake, TOutput>({
  fetch,
  mapContext,
  isIntakeDisallowed,
}: {
  fetch: CancellationReasonFetchHandler<TOutput>;
  mapContext: (output: TOutput) => Partial<CancellationReasonMachineContextBase<TReason, TIntake>>;
  isIntakeDisallowed?: (output: TOutput) => boolean;
}) => {
  type GetReasonsEvent = {
    type: 'GET_REASONS';
    config: MachineConfig;
    values: CancellationReasonFetchValues;
  };

  type MachineEvent = GetReasonsEvent | { type: 'RETRY_GET_REASONS' };

  type Context = CancellationReasonMachineContextBase<TReason, TIntake> & {
    lastGetReasonsEvent?: GetReasonsEvent;
  };

  interface InputType {
    event?: GetReasonsEvent;
    context: Context;
  }

  return setup({
    types: {} as {
      context: Context;
      events: MachineEvent;
      actors: {
        getData: {
          input: InputType;
          output: TOutput;
        };
      };
    },
    actions: {
      clearErrorMessage: assign({ errorMessage: undefined }),
    },
    actors: {
      getData: fromPromise<TOutput, InputType>(({ input }) => {
        const { event } = input;
        if (event?.type !== 'GET_REASONS') {
          return Promise.reject(new Error('Invalid event type'));
        }
        return fetch(event.config, event.values);
      }),
    },
  }).createMachine({
    id: 'reason-machine',
    initial: 'IDLE',
    context: {
      errorMessage: undefined,
      reasonStatus: undefined,
      reasons: undefined,
      discontinueDisallowReason: undefined,
      remainingMonths: undefined,
      intake: undefined,
      lastGetReasonsEvent: undefined,
    },
    states: {
      IDLE: {
        entry: 'clearErrorMessage',
        on: {
          GET_REASONS: {
            target: 'FETCHING',
            actions: assign({
              lastGetReasonsEvent: ({ event }) => event,
            }),
          },
        },
      },
      FETCHING: {
        invoke: {
          src: 'getData',
          input: ({ event, context }) => ({
            event: event.type === 'GET_REASONS' ? event : context.lastGetReasonsEvent,
            context,
          }),
          onDone: [
            {
              guard: ({ event }) => isIntakeDisallowed?.(event.output) === true,
              target: 'INTAKE_DISALLOWED',
              actions: [assign(({ event }) => mapContext(event.output))],
            },
            {
              target: 'SUCCESS',
              actions: [assign(({ event }) => mapContext(event.output))],
            },
          ],
          onError: {
            target: 'ERROR',
            actions: assign({
              errorMessage: ({ event }) => (event.error as Error).message,
            }),
          },
        },
      },
      SUCCESS: {
        entry: 'clearErrorMessage',
      },
      INTAKE_DISALLOWED: {},
      ERROR: {
        on: {
          RETRY_GET_REASONS: { target: 'FETCHING' },
        },
      },
    },
  });
};

export const createCancellationReasonMachine = (productType: Products_Relocations_DiscontinueProductType) =>
  createCancellationReasonMachineWithFetch<
    Products_FineCalculation_ReasonModel,
    Products_FineCalculation_DiscontinueIntakeModel,
    ProductReasonFetchOutput
  >({
    fetch: (config, values) =>
      Promise.all([getIntakeService(productType, config, values), getReasonsService(productType, config)]).then(
        ([intakeData, reasonsData]) => ({ intakeData, reasonsData }),
      ),
    mapContext: ({ intakeData, reasonsData }) => getValuesFromAttributes(intakeData?.data, reasonsData),
  });
